/**
 * reviewPromptService — non-React helpers for the review-solicitation feature.
 *
 * These run outside React render (query-execution success branch, app boot), so
 * they use the fire-and-forget analytics pattern and read the store via
 * `getState()` rather than hooks.
 */

import { toast } from '../../../components/ui/toaster';
import { openExternalUrl } from '../../help-feedback/services/feedbackApi';
import { reviewUrl } from '../reviewPromptConstants';
import { useReviewPromptStore } from '../store/reviewPromptStore';

/** Fire-and-forget analytics from a non-React context (never throws). */
function trackFireForget(
  name: string,
  data?: Record<string, string | number | boolean | null>,
): void {
  window.electronAPI?.analytics?.track(name, data)?.catch(() => {
    // Analytics must never affect UX — swallow errors.
  });
}

/**
 * Called from the query-execution success branch. Records the successful query
 * and, IFF this crossed the 100-query milestone while pending, fires the
 * one-time celebratory review toast. The store arms + persists itself before
 * `recordSuccessfulQuery()` returns true, so the toast fires at most once ever
 * (crash-safe).
 */
export function maybeShowMilestoneToast(): void {
  const armed = useReviewPromptStore.getState().recordSuccessfulQuery();
  if (!armed) return;

  trackFireForget('feature:review_prompt_shown', { surface: 'toast' });

  toast.default({
    title: "🎉 That's 100 queries in Jam SQL Studio",
    description: 'Mind sharing your experience? A short review helps other developers find it.',
    duration: 20000,
    testId: 'review-prompt-toast',
    action: {
      label: 'Write a review',
      onClick: () => {
        trackFireForget('feature:review_prompt_click', { surface: 'toast' });
        useReviewPromptStore.getState().markClicked();
        void openExternalUrl(reviewUrl('milestone-toast'));
      },
    },
  });
}

// Module-level guard: recordAppSession() must count once per app boot, not once
// per component mount (StrictMode double-invokes effects; HMR can re-run entry).
let sessionRecorded = false;

/**
 * Record exactly one app session for this boot. Safe to call more than once —
 * only the first call reaches the store.
 */
export function recordAppSessionOnce(): void {
  if (sessionRecorded) return;
  sessionRecorded = true;
  useReviewPromptStore.getState().recordAppSession();
}
